/**
 * clientColumns.js - Column definitions for client table
 *
 * This file separates column configuration from the component,
 * making it easier to maintain and modify column behavior.
 * Filter metadata enables auto-generated filter options.
 */

import React from "react";
import { createColumn } from "../common/Utils/columnDefinitions";

/**
 * Clickable link styles for interactive cells
 */
const clickableLinkStyle = {
  cursor: "pointer",
  color: "#0066cc",
  textDecoration: "none",
};

/**
 * Creates the column definitions for the client table
 * @param {Object} handlers - Event handlers from the parent component
 * @param {function} handlers.setClicked - Handler for row click actions
 * @param {function} handlers.handleAttestBtnDisable - Check if attest button should be disabled
 * @returns {Array} Array of column definitions with filter metadata
 */
export function createClientColumns(handlers) {
  const { setClicked, handleAttestBtnDisable } = handlers;

  return [
    createColumn({
      key: "client-name",
      label: "Client Name(s)",
      filterable: false, // Use search bar instead of dropdown filter (too many unique values)
      searchable: true, // Can be searched via text search
      filterType: "contains",
      content: (client) => (
        <span
          style={clickableLinkStyle}
          className="clickable-link"
          onClick={() => setClicked({ column: "client", data: client })}
          onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
          onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
        >
          {client["client-name"]}
        </span>
      ),
    }),

    createColumn({
      key: "advisor-name",
      label: "Advisor(s)",
      filterable: true, // Enable filter dropdown for FP selection
      filterKey: "financial-professional", // Use existing filter state key
      useCustomOptions: true, // Use financialProfessionals prop instead of auto-generating from data
      searchable: true, // Can also be searched via text search
      filterType: "contains",
      content: (client) => {
        const value =
          client[client["advisor-name"] ? "advisor-name" : "group-id"];
        const parts = value.replaceAll("/", ";").split(";");
        return parts.map((g, index) => (
          <span key={index}>
            {g}
            {parts.length > 1 && index < parts.length - 1 ? (
              <hr style={{ margin: "0", padding: "0" }} />
            ) : undefined}
          </span>
        ));
      },
    }),

    createColumn({
      key: "attestation-status",
      label: "Status",
      filterable: true,
      filterType: "exact",
      filterKey: "statusFilter",
      filterAllValue: "All",
      content: (client) => (
        <span
          style={clickableLinkStyle}
          className="clickable-link"
          onClick={() => setClicked({ column: "history", data: client })}
          onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
          onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
        >
          {client["attestation-status"]}
        </span>
      ),
    }),

    createColumn({
      path: "review-date",
      label: "Date of Review",
      filterable: false, // Date field - don't use dropdown filter
      sortable: true,
      sortFn: (a, b) => {
        const parse = (val) => {
          if (!val || val === "-") return null;
          const ts = Date.parse(val);
          return isNaN(ts) ? null : ts;
        };
        const aTs = parse(a["review-date"]);
        const bTs = parse(b["review-date"]);
        if (aTs === null && bTs === null) return 0;
        if (aTs === null) return 1; // non-dates sort to end
        if (bTs === null) return -1;
        return aTs - bTs;
      },
    }),

    createColumn({
      path: "review-structure",
      label: "Review Structure",
      filterable: true, // Auto-generate dropdown from data
      filterType: "exact",
    }),

    createColumn({
      path: "account-substantial-change",
      label: "Substantial Changes?",
      filterable: true, // Auto-generate dropdown from data
      filterType: "exact",
    }),

    createColumn({
      path: "account-r-t-a-changes",
      label: "Needs/Objectives Changed?",
      filterable: true, // Auto-generate dropdown from data
      filterType: "exact",
    }),

    createColumn({
      path: "reg-b-i-changes",
      label: "Best Interest?",
      filterable: true, // Auto-generate dropdown from data
      filterType: "exact",
    }),

    // Action column (non-filterable, non-sortable)
    createColumn({
      key: "actions",
      label: "",
      filterable: false,
      sortable: false, // Actions column should not be sortable
      content: (client) => (
        <div className="row" style={{ margin: "auto" }}>
          {/* TODO: QOL Phase 2 - Re-enable dropdown with Request Change, View History */}
          <button
            className="btn btn-attest"
            onClick={() => setClicked({ column: "attest", data: client })}
            disabled={handleAttestBtnDisable(client)}
          >
            {client.serverUpdateInProgress && (
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="sr-only"></span>
              </div>
            )}
            Attest
          </button>
        </div>
      ),
    }),
  ];
}

/**
 * Default status filter options (used when data is empty or as fallback)
 * These represent the known attestation status values in the system.
 */
export const DEFAULT_STATUS_OPTIONS = [
  "All",
  "Submitted",
  "Partially Submitted",
  "Not Submitted",
];
